import React, { useCallback, useRef } from 'react';
import clsx from 'clsx';
import {
    Box, Checkbox, DialogContent, DialogTitle,
    Divider, FormControlLabel, Icon, IconButton, InputBase,
    Paper, TextField, Typography,
} from '@material-ui/core';
import { DisabledFormWrapper } from 'app/main/util/clusterUtils';

import * as formHelpers from './formHelpers';
import { isFlexInstance } from './formHelpers';
import useClusterForm from '../hooks/useClusterForm';
import ClusterFormHeader from '../components/ClusterFormHeader';
import AdvancedSectionsBlock from '../components/AdvancedSectionsBlock';
import QuotaWarningBanner from '../components/QuotaWarningBanner';
import BasicInformationSection from '../components/BasicInformationSection';
import ResourceManagerConfiguration from '../components/ResourceManagerConfiguration';
import StorageSettingsSection from '../components/StorageSettingsSection';
import DescriptionSection from '../components/DescriptionSection';
import CredentialSelector from '../components/CredentialSelector';
import RegionSelector from '../components/RegionSelector';
import InstanceSizeSelector from '../components/InstanceSizeSelector';
import SectionCard from '../components/SectionCard';
import { ZoneAvailabilityIndicator } from '../components/ZoneAvailabilityComponents';
import { inputGroupStyle, columnContainerStyle, columnStyle } from '../components/formLayoutStyles';
import ComputeNodesSection from '../components/ComputeNodesSection';
import ComputeNodeGroupFields from '../components/ComputeNodeGroupFields';

const extraValidation = (form) => {
    if (!form.availabilityDomain) return false;
    if (isFlexInstance(form.serverInstanceId)) {
        if (!form.serverOcpus || !form.serverMem) return false;
        if (parseInt(form.serverMem, 10) < parseInt(form.serverOcpus, 10)) return false;
    }
    return true;
};

const OracleCluster = (props) => {
    const pkgRef = useRef(null);

    const {
        form, setForm, setField, advanced, setAdvanced,
        classes, loading, marketplace,
        disableInputs, isFormValid, formInvalidReason, volSizeLimits,
        availability, quotas, handleClose, handleSubmit, handleEdit,
    } = useClusterForm({
        provider: 'oracle',
        props,
        formHelpers,
        extraValidation,
        zoneField: 'availabilityDomain',
    });

    const {
        isLoading: isAvailabilityLoading, error: availabilityError,
        unableToVerify, selectedZoneAvailable, selectedZoneMissing,
    } = availability;

    // Oracle: reset Flex fields when switching to non-Flex instance
    const handleServerInstanceChange = useCallback((instanceId) => {
        const changes = { serverInstanceId: instanceId };
        if (!isFlexInstance(instanceId)) {
            changes.serverOcpus = 1;
            changes.serverMem = 1;
        }
        setField(changes);
    }, [setField]);

    const handleComputeInstanceChange = useCallback((index, instanceId) => {
        const updatedGroups = [...(form?.nodeGroups || [])];
        const groupUpdate = { ...updatedGroups[index], instance_id: instanceId };
        if (!isFlexInstance(instanceId)) {
            groupUpdate.ocpus = 1;
            groupUpdate.mem = 1;
        }
        updatedGroups[index] = groupUpdate;
        setField('nodeGroups', updatedGroups);
    }, [form?.nodeGroups, setField]);

    // Package management
    const addPackage = useCallback(() => {
        if (!pkgRef.current?.value) return;
        setField('packages', [...(form?.packages || []), pkgRef.current.value]);
        pkgRef.current.value = '';
    }, [form?.packages, setField]);

    const removePackage = useCallback((pkg) => {
        setField('packages', (form?.packages || []).filter(p => p !== pkg));
    }, [form?.packages, setField]);

    // Oracle compute node render prop — shared fields + Flex OCPU/mem
    const renderOracleGroupFields = useCallback((group, index, { updateGroup }) => (
        <ComputeNodeGroupFields
            group={group} index={index} updateGroup={updateGroup}
            provider="oracle" imageMode={form?.imageMode} maxCount={250}
            onInstanceChange={handleComputeInstanceChange}
            allowedInstanceIds={marketplace.allowedComputeIds}
            disableStackFields={marketplace.disableStackFields}
            disabled={disableInputs}
        >
            {isFlexInstance(group.instance_id) && (
                <div style={inputGroupStyle}>
                    <TextField
                        label="Node OCPUs" type="number" variant="outlined"
                        className={clsx(classes.input)} style={{ flex: 1 }}
                        value={group.ocpus || 1}
                        onChange={(e) => updateGroup(index, 'ocpus', parseInt(e.target.value, 10) || 1)}
                        inputProps={{ min: 1 }}
                        disabled={disableInputs}
                    />
                    <TextField
                        label="Node Memory (GB)" type="number" variant="outlined"
                        className={clsx(classes.input)} style={{ flex: 1 }}
                        value={group.mem || 1}
                        onChange={(e) => updateGroup(index, 'mem', parseInt(e.target.value, 10) || 1)}
                        inputProps={{ min: 1 }}
                        disabled={disableInputs}
                        error={parseInt(group.mem || 0, 10) < parseInt(group.ocpus || 0, 10)}
                        helperText={parseInt(group.mem || 0, 10) < parseInt(group.ocpus || 0, 10) ? 'Memory must be >= OCPUs' : ''}
                    />
                </div>
            )}
        </ComputeNodeGroupFields>
    ), [form?.imageMode, marketplace, handleComputeInstanceChange, disableInputs, classes.input]);

    return (
        <React.Fragment>
            {form && (
                <React.Fragment>
                    <DialogTitle className={clsx(classes.modalHeader, 'h-54 min-h-54 p-20')}>
                        <ClusterFormHeader
                            provider="oracle" providerLabel="Oracle Cloud" marketplace={marketplace}
                            advanced={advanced} onToggleAdvanced={() => setAdvanced(!advanced)}
                            isFormValid={isFormValid} formInvalidReason={formInvalidReason} loading={loading} disableInputs={disableInputs}
                            formType={props.type} onSave={handleSubmit} onEdit={handleEdit} onClose={handleClose}
                        />
                    </DialogTitle>

                    <QuotaWarningBanner quotas={quotas} />

                    <DialogContent className={clsx(classes.content, 'p-16')}>
                        <DisabledFormWrapper isDisabled={disableInputs}>
                            <div className="!w-full">

                                <BasicInformationSection
                                    form={form} setField={setField} provider="oracle"
                                    allowedOsTypes={marketplace.allowedOsTypes || undefined}
                                    disableStackFields={marketplace.disableStackFields}
                                />

                                {/* Infrastructure + Resource Manager (two columns) */}
                                <div style={columnContainerStyle}>
                                    <div style={columnStyle}>
                                        <SectionCard
                                            icon="cloud" title="Infrastructure" fillHeight
                                            action={form.availabilityDomain ? (
                                                <ZoneAvailabilityIndicator
                                                    isLoading={isAvailabilityLoading} isAvailable={selectedZoneAvailable}
                                                    missingInstances={selectedZoneMissing} error={availabilityError}
                                                    unableToVerify={unableToVerify}
                                                />
                                            ) : null}
                                        >
                                            <div style={inputGroupStyle}>
                                                <CredentialSelector provider="oracle" value={form.credentialId}
                                                    onChange={(id) => setField('credentialId', id)} />
                                                <InstanceSizeSelector provider="oracle" value={form.serverInstanceId || ''}
                                                    onChange={handleServerInstanceChange}
                                                    allowedInstanceIds={marketplace.config?.instance_type}
                                                    label="Head Node Size" />
                                            </div>
                                            <div style={inputGroupStyle}>
                                                <RegionSelector provider="oracle" value={form.regionId || ''}
                                                    onChange={(id) => setField('regionId', id)}
                                                    allowedRegionIds={marketplace.config?.allowed_regions?.length > 0
                                                        ? marketplace.config.allowed_regions : undefined}
                                                />
                                                <TextField
                                                    className={clsx(classes.input)} style={{ flex: 1 }}
                                                    label="Availability Zone" variant="outlined" required
                                                    value={form.availabilityDomain || ''}
                                                    onChange={(e) => setField('availabilityDomain', e.target.value)}
                                                    placeholder="Example: ABCD:US-ASHBURN-AD-1"
                                                />
                                                {advanced && (
                                                    <TextField
                                                        className={clsx(classes.input)} style={{ flex: 1 }}
                                                        label="Security List ID" variant="outlined"
                                                        value={form.securityGroupId || ''}
                                                        onChange={(e) => setField('securityGroupId', e.target.value)}
                                                        placeholder="Example: ocid1.securitylist.oc1.iad.aaaa"
                                                    />
                                                )}
                                            </div>
                                        </SectionCard>
                                    </div>
                                    <div style={columnStyle}>
                                        <ResourceManagerConfiguration
                                            form={form} setField={setField}
                                            hideBursting={marketplace.isMarketStack}
                                            lockManager={Boolean(marketplace.config?.resource_manager)}
                                        />
                                    </div>
                                </div>

                                {/* Flex Instance Configuration — only for Flex instances */}
                                {isFlexInstance(form.serverInstanceId) && (
                                    <SectionCard icon="memory" title="Flex Instance Configuration">
                                        <div style={inputGroupStyle}>
                                            <TextField
                                                className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="Server Cores (OCPUs)" type="number" variant="outlined" required
                                                value={form.serverOcpus || 1}
                                                onChange={(e) => setField('serverOcpus', parseInt(e.target.value, 10) || 1)}
                                                inputProps={{ min: 1 }}
                                            />
                                            <TextField
                                                className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="Server Memory (GB)" type="number" variant="outlined" required
                                                value={form.serverMem || 1}
                                                onChange={(e) => setField('serverMem', parseInt(e.target.value, 10) || 1)}
                                                inputProps={{ min: 1 }}
                                                error={parseInt(form.serverMem || 0, 10) < parseInt(form.serverOcpus || 0, 10)}
                                                helperText={parseInt(form.serverMem || 0, 10) < parseInt(form.serverOcpus || 0, 10) ? 'Memory must be >= OCPUs' : ''}
                                            />
                                        </div>
                                    </SectionCard>
                                )}

                                {/* Advanced: Network + Storage */}
                                {advanced && (
                                    <SectionCard icon="tune" title="Advanced Settings">
                                        <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: '12px' }}>Network Configuration</Typography>
                                        <div style={inputGroupStyle}>
                                            <TextField
                                                className={clsx(classes.input)} style={{ flex: 1 }}
                                                label={form.serverIsPrivate ? '*LPG Name' : 'LPG Name'}
                                                variant="outlined"
                                                value={form.lpg || ''}
                                                onChange={(e) => setField('lpg', e.target.value)}
                                                placeholder="Local Peering Gateway Name"
                                            />
                                            <TextField
                                                className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="VCN CIDR" variant="outlined"
                                                required={Boolean(form.vcnId)}
                                                value={form.vcnCidr || ''}
                                                onChange={(e) => setField('vcnCidr', e.target.value)}
                                                placeholder="Example: 172.31.0.0/16"
                                            />
                                            <TextField
                                                className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="Local CIDR" variant="outlined"
                                                value={form.localCidr || ''}
                                                onChange={(e) => setField('localCidr', e.target.value)}
                                                placeholder="Example: 10.0.0.0/8"
                                            />
                                            <TextField
                                                className={clsx(classes.input)} style={{ flex: 1 }}
                                                label="VCN ID" variant="outlined"
                                                value={form.vcnId || ''}
                                                onChange={(e) => setField('vcnId', e.target.value)}
                                            />
                                            <FormControlLabel
                                                style={{ alignSelf: 'center' }}
                                                control={
                                                    <Checkbox
                                                        checked={form.serverIsPrivate || false}
                                                        onChange={(e) => setField('serverIsPrivate', e.target.checked)}
                                                        color="primary"
                                                    />
                                                }
                                                label="Private IP"
                                            />
                                        </div>

                                        <Divider style={{ margin: '16px 0' }} />

                                        <StorageSettingsSection
                                            form={form} setField={setField} provider="oracle"
                                            volSizeLimits={volSizeLimits}
                                            shareOptions={[
                                                { value: 'nfs', label: 'NFS' },
                                                { value: 'beegfs', label: 'BeeGFS' },
                                            ]}
                                        />
                                    </SectionCard>
                                )}

                                <AdvancedSectionsBlock
                                    form={form} setForm={setForm} setField={setField}
                                    provider="oracle" advanced={advanced}
                                    marketplace={marketplace} disableInputs={disableInputs}
                                />

                                {/* Packages */}
                                {advanced && (
                                    <SectionCard icon="inventory_2" title="Packages">
                                        <Paper style={{ display: 'flex', alignItems: 'center', padding: '2px 4px', marginBottom: '16px' }}>
                                            <InputBase
                                                inputRef={pkgRef}
                                                style={{ flex: 1, marginLeft: '8px' }}
                                                placeholder="Add a package"
                                            />
                                            <IconButton onClick={addPackage}>
                                                <Icon>add</Icon>
                                            </IconButton>
                                        </Paper>
                                        {(form.packages || []).map((pkg, i) => (
                                            <Box key={i} display="flex" alignItems="center" mb={0.5}>
                                                <Typography variant="body2" style={{ flex: 1 }}>{pkg}</Typography>
                                                <IconButton size="small" onClick={() => removePackage(pkg)}>
                                                    <Icon fontSize="small">delete</Icon>
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </SectionCard>
                                )}

                                {/* Compute Nodes */}
                                <ComputeNodesSection
                                    form={form} setField={setField}
                                    disabled={disableInputs}
                                    maxGroups={250}
                                    renderGroupFields={renderOracleGroupFields}
                                />

                                <DescriptionSection form={form} setField={setField} />
                            </div>
                        </DisabledFormWrapper>
                    </DialogContent>
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export default OracleCluster;
